import React, { Suspense } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { useJdpSectionExpanded } from '../../../../../hooks/useJdpSectionExpanded';
import { Link } from '../../../../../components/Link';
import { HitCompanyInsightsCueLink } from '../../../../../components/Hit/HitCompanyInsightsCueLink';
import { adjustBrandColor } from '../../../../../utils/brand-color/adjust-brand-color';
import { shouldLinkCompanyProfile } from '../../../../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../../../../utils/routing/company-profile-url-path';
import { resolveSearchHitIsTyomarkkinatori } from '../../../../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import { buildFullCompanyAddress, formatPostalAddressLine } from '../../../../../utils/geocoding/match-job-location-with-company-address';
import { TYOMARKKINATORI_PURPLE } from '../utils/constants';
import { OverviewSectionHeader, JobDetailsSection, SalaryDistributionChart, MatchScoreBreakdown, InternalLinksSection } from '../components';
import { SimilarJobsSection } from '../components/SimilarJobsSection';
import { BrandRail } from './BrandRail';
import { MetaGrid } from './MetaGrid';
import { CompanyCard } from './CompanyCard';
import { CollectiveAgreementNotice } from './CollectiveAgreementNotice';
import type { OverviewSectionHeaderProps } from '../components/OverviewSectionHeader';
import type { DisplayJob } from '../../../../../types';

// Lazy-loaded because it pulls in the entire mapbox-gl dependency (~700kB JS + 5kB CSS
// asset). The user must open the "Location" pane to mount the map, so lazy-loading is safe.
const CompanyLocationMap = React.lazy(() =>
  import('../../../../../components/CompanyLocationMap').then((m) => ({ default: m.CompanyLocationMap })),
);

export interface JobDetailPageV2Props {
  displayJob: DisplayJob;
  title?: string;
  normalisedTitle?: string;
  companyId?: string | null;
  location?: string;
  remoteness?: string;
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
  collectiveAgreementId?: string | null;
  collectiveAgreementName?: string | null;
  collectiveAgreementMatchType?: 'explicit' | 'inferred' | 'none' | null;
  collectiveAgreementReasoning?: string | null;
  jobLocale: string;
  displayDescription?: string;
  overviewHeaderProps: OverviewSectionHeaderProps;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  /** When provided, the brand rail's apply button renders a secondary "Tailor my CV" action. */
  onTailorCv?: () => void;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  matchScoreData: import('../../../../../utils/data/calculate-job-match-score').MatchScoreBreakdown | null;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  locationForChart?: string[];
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  salaryListingProvenance?: import('../../../../../utils/job-detail/resolve-job-salary-listing-provenance').JobSalaryListingProvenance;
  optionalLanguages?: string[];
  perks?: string[];
  jobDetailsRef: React.RefObject<HTMLDivElement>;
  currentLocaleTrends?: {
    roleDistribution: Record<string, number>;
    roleLevelDistribution?: Record<string, number>;
    locationDistribution?: Record<string, number>;
  };
  allLinks: unknown[];
  effectiveSimilarJobs: unknown[];
  similarJobsSeeAllHref?: string;
}

/**
 * V2 (brand color panel) redesign of the job detail page — replaces just the core JobPosting
 * card (breadcrumb/language-switcher/footer/engagement-strip/tailor-cv-modal stay shared,
 * rendered by +Page.tsx around this component regardless of variant). Reuses OverviewSectionHeader,
 * JobDetailsSection, SalaryDistributionChart, MatchScoreBreakdown, SimilarJobsSection, and
 * InternalLinksSection as-is (all fully prop-driven, already tested, doing complex interactive
 * work that would be wasteful and risky to duplicate) — only the brand rail, meta grid, and
 * company card (most-hired-roles bars + role-level mix, replacing the tabbed donut charts) are
 * new, matching the design handoff's structural identity.
 */
export function JobDetailPageV2(props: JobDetailPageV2Props): React.ReactElement {
  const intl = useIntl();
  const {
    displayJob,
    title,
    normalisedTitle,
    companyId,
    location,
    remoteness,
    field,
    jobType,
    normalisedRoleLevel,
    requiredLanguages,
    peopleManagementTier,
    educationLevel,
    minimumYearsOfExperience,
    collectiveAgreementId,
    collectiveAgreementName,
    collectiveAgreementMatchType,
    collectiveAgreementReasoning,
    jobLocale,
    displayDescription,
    overviewHeaderProps,
    applyUrl,
    isApplied,
    setAppliedJobState,
    onTailorCv,
    formattedPostedDate,
    postedDaysAgo,
    formattedLastSeenDate,
    formattedApplicationDate,
    formattedEstimatedExpiryDate,
    matchScoreData,
    salaryRangeMin,
    salaryRangeMax,
    locationForChart,
    currency,
    salaryPeriod,
    salaryListingProvenance,
    optionalLanguages,
    perks,
    jobDetailsRef,
    currentLocaleTrends,
    allLinks,
    effectiveSimilarJobs,
    similarJobsSeeAllHref,
  } = props;

  const isTyomarkkinatori = resolveSearchHitIsTyomarkkinatori(displayJob as unknown as Record<string, unknown>);
  // Työmarkkinatori listings always brand purple, same override the SRP card and V1 detail
  // page apply — without it this panel falls back to the neutral color since TMT companies'
  // raw brandColour (often black/unset) reads as achromatic to adjustBrandColor.
  const adjustedBrand = adjustBrandColor(isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : displayJob.brandColour);
  const companyDisplayName = displayJob.companyName ?? companyId ?? '';
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId: companyId ?? '', isTyomarkkinatori });
  const companyProfileHref = canLinkCompanyProfile && companyId ? companyProfileUrlPath(jobLocale as 'en' | 'fi' | 'sv', companyId) : '';
  const companyLinkTitle = intl.formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile — data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplayName },
  );
  const insightsCueLabel = intl.formatMessage({
    id: 'hit.companyInsights.cue',
    defaultMessage: 'Company insights',
    description: 'Short label under the employer name on a job card; the block links to the company profile',
  });
  const salaryStats = displayJob.companySalaryStats || (displayJob as unknown as { salaryStats?: unknown }).salaryStats;
  const [sectionExpanded, toggleSectionExpanded] = useJdpSectionExpanded();

  const companyAddressLine = formatPostalAddressLine(
    buildFullCompanyAddress({
      registryStreet: displayJob.registryStreet,
      registryBuildingNumber: displayJob.registryBuildingNumber,
      registryPostCode: displayJob.registryPostCode,
      registryCity: displayJob.registryCity,
      address_fi: displayJob.address_fi,
      address_sv: displayJob.address_sv,
    }),
  );
  const hasRealCompanyAddress = Boolean(displayJob.companyGeocoordinates && companyAddressLine);

  return (
    <main id="job-main-content" className="mt-6">
      <div
        className="flex flex-col lg:flex-row items-stretch overflow-hidden rounded-[24px] bg-white dark:bg-[#1F1509] shadow-[0_1px_3px_rgba(22,21,15,0.08)]"
      >
        <BrandRail
          displayJob={displayJob}
          panelColor={adjustedBrand.panel}
          panelOn={adjustedBrand.on}
          applyUrl={applyUrl}
          isApplied={isApplied}
          setAppliedJobState={setAppliedJobState}
          onTailorCv={onTailorCv}
          formattedPostedDate={formattedPostedDate}
          postedDaysAgo={postedDaysAgo}
          formattedLastSeenDate={formattedLastSeenDate}
          formattedApplicationDate={formattedApplicationDate}
          formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
        />

        <div className="flex-1 min-w-0 flex flex-col gap-5 p-6 md:p-8">
          {/* Header row: company link, H1 title/subtitle, match ring + bookmark + overflow menu.
              Stacked on mobile — side by side, the action-icon cluster's fixed width otherwise
              squeezes the title column narrow enough to force short titles onto two lines. */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <div className="mb-1.5 text-xs font-semibold text-[#8C8474]">
                {companyProfileHref && canLinkCompanyProfile ? (
                  <Link href={companyProfileHref} locale={jobLocale} className="text-inherit no-underline border-b border-current" title={companyLinkTitle}>
                    {fixMojibake(companyDisplayName)}
                  </Link>
                ) : (
                  fixMojibake(companyDisplayName)
                )}
                {companyProfileHref && canLinkCompanyProfile ? (
                  <>
                    {' · '}
                    <HitCompanyInsightsCueLink variant="under-title" href={companyProfileHref} locale={jobLocale} title={companyLinkTitle} insightsCue={insightsCueLabel} />
                  </>
                ) : null}
              </div>
              <h1 className="m-0 [font-family:'Space_Grotesk',sans-serif] text-[26px] md:text-[30px] font-bold leading-tight text-[#16150F] dark:text-[#F3EFE4]">
                {fixMojibake(normalisedTitle || title || '')}
              </h1>
              {title && title !== normalisedTitle ? (
                <div className="mt-1 text-sm text-[#6E6659] dark:text-[#B0A896]">{fixMojibake(title)}</div>
              ) : null}
            </div>
            <div className="flex flex-none items-center gap-1">
              <OverviewSectionHeader
                {...overviewHeaderProps}
                ringColorOverride={adjustedBrand.panel}
                labelColorOverride="#16150F"
              />
            </div>
          </div>

          <MetaGrid
            location={location}
            remoteness={remoteness}
            field={field}
            jobType={jobType}
            normalisedRoleLevel={normalisedRoleLevel}
            requiredLanguages={requiredLanguages}
            peopleManagementTier={peopleManagementTier}
            educationLevel={educationLevel}
            minimumYearsOfExperience={minimumYearsOfExperience}
          />

          <CollectiveAgreementNotice
            collectiveAgreementId={collectiveAgreementId}
            collectiveAgreementName={collectiveAgreementName}
            collectiveAgreementMatchType={collectiveAgreementMatchType}
            collectiveAgreementReasoning={collectiveAgreementReasoning}
            jobLocale={jobLocale}
          />

          {salaryStats || displayJob.locationSalaryStats || displayJob.allSalaryStats ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <div className="mb-1 flex items-center gap-2">
                <div className="[font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                  <FormattedMessage id="jobDetailPageV2.salaryCompares" defaultMessage="How this salary compares" description="Heading for the salary benchmark section on the redesigned job detail page" />
                </div>
                <button
                  type="button"
                  onClick={() => toggleSectionExpanded('salaryContext')}
                  className="text-[#8C8474] hover:brightness-90 transition-transform cursor-pointer p-0.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  aria-label={sectionExpanded.salaryContext
                    ? intl.formatMessage({ id: 'jobDetailPageV2.salaryCompares.collapse', defaultMessage: 'Collapse salary comparison', description: 'Button label to collapse/hide the salary comparison section' })
                    : intl.formatMessage({ id: 'jobDetailPageV2.salaryCompares.expand', defaultMessage: 'Expand salary comparison', description: 'Button label to expand/show the salary comparison section' })}
                >
                  {sectionExpanded.salaryContext ? <CaretUp className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                </button>
              </div>
              {sectionExpanded.salaryContext && (
                <SalaryDistributionChart
                  salaryStats={salaryStats as never}
                  locationSalaryStats={displayJob.locationSalaryStats}
                  allSalaryStats={displayJob.allSalaryStats}
                  jobSalaryMin={salaryRangeMin}
                  jobSalaryMax={salaryRangeMax}
                  currency={currency}
                  salaryPeriod={salaryPeriod}
                  locale={jobLocale}
                  jobLocation={locationForChart}
                  jobTitle={normalisedTitle || title}
                  jobRoleLevel={normalisedRoleLevel}
                  jobSalaryListingProvenance={salaryListingProvenance}
                />
              )}
            </div>
          ) : null}

          {displayJob.tldr ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <div className="mb-2.5 [font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                <FormattedMessage id="jobDetailPageV2.tldr" defaultMessage="At a glance" description="Heading for the AI-generated TL;DR summary section on the redesigned job detail page" />
              </div>
              <p className="m-0 text-[13.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882] whitespace-pre-line">{fixMojibake(displayJob.tldr)}</p>
            </div>
          ) : null}

          {displayDescription ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <div className="mb-2.5 [font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                <FormattedMessage id="jobDetailPageV2.aboutRole" defaultMessage="About the role" description="Heading for the job description section on the redesigned job detail page" />
              </div>
              <p className="m-0 text-[13.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882] whitespace-pre-line">{fixMojibake(displayDescription)}</p>
            </div>
          ) : null}

          {matchScoreData ? (
            <MatchScoreBreakdown
              matchScore={matchScoreData}
              expanded={sectionExpanded.matchBreakdown}
              onToggleExpanded={() => toggleSectionExpanded('matchBreakdown')}
            />
          ) : null}

          <JobDetailsSection displayJob={displayJob} optionalLanguages={optionalLanguages} perks={perks} jobDetailsRef={jobDetailsRef} />

          <CompanyCard displayJob={displayJob} companyId={companyId} panelColor={adjustedBrand.panel} jobLocale={jobLocale} currentLocaleTrends={currentLocaleTrends} />

          {hasRealCompanyAddress ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <div className="mb-1 flex items-center gap-2">
                <div className="[font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                  <FormattedMessage id="jobDetailPageV2.location" defaultMessage="Location" description="Heading for the company location map section on the redesigned job detail page" />
                </div>
                <button
                  type="button"
                  onClick={() => toggleSectionExpanded('location')}
                  className="text-[#8C8474] hover:brightness-90 transition-transform cursor-pointer p-0.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  aria-label={sectionExpanded.location
                    ? intl.formatMessage({ id: 'jobDetailPageV2.location.collapse', defaultMessage: 'Collapse location map', description: 'Button label to collapse/hide the company location map' })
                    : intl.formatMessage({ id: 'jobDetailPageV2.location.expand', defaultMessage: 'Expand location map', description: 'Button label to expand/show the company location map' })}
                >
                  {sectionExpanded.location ? <CaretUp className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                </button>
              </div>
              {sectionExpanded.location && (
                <Suspense fallback={<div className="w-full h-[400px] bg-jc-page/50 rounded-xl animate-pulse" aria-hidden="true" />}>
                  <CompanyLocationMap
                    coordinates={displayJob.companyGeocoordinates!}
                    companyName={companyDisplayName}
                    address={companyAddressLine}
                    companyId={companyId ?? undefined}
                  />
                </Suspense>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-6">
        <SimilarJobsSection similarJobs={effectiveSimilarJobs as never} seeAllHref={similarJobsSeeAllHref} />
      </div>
      <div className="mt-6">
        <InternalLinksSection links={allLinks as never} />
      </div>
    </main>
  );
}
